// Listing modes for ATA — Dashboard / ATA Chapters / MCIs.
//
// Modes are ROUTES (PATTERNS.md §"Module weight"), not `useState`.
// Mirrored from src/personnel/modes.ts and src/blank/BlankLayout.tsx.
//
//   <base>/dashboard  → ATADashboard (airworthiness gap analysis dashboard)
//   <base>/overview   → redirect to <base>/dashboard (its old name)
//   <base>/chapters   → ATAChaptersList (ATA 100 reference table)
//   <base>/tasks      → ATATasksList (the working MCI register)
//
// The `tasks` KEY is the route segment and is deliberately unchanged —
// /app/ata/tasks is bookmarked and linked from the Hub. Only the LABEL moves.

import type { ModeDescriptions } from "../components/BreadcrumbDropdownMenu";
import { migrateModeSegment } from "../components/renamedModeSegments";

/**
 * Mode segment -> breadcrumb / dropdown label. Keys ARE the child route paths.
 */
export const MODE_LABELS: { [segment: string]: string } = {
  dashboard: "Dashboard",
  chapters: "ATA Chapters",
  tasks: "MCIs",
};

/**
 * Mode segment -> the grey line under its label in the breadcrumb dropdown
 * (BP-85, H76: "ALL model switchers should have name and description the same
 * as demand"). Same keys as the label map; each read off what the mode's
 * component renders, not paraphrased from the label.
 */
export const MODE_DESCRIPTIONS: ModeDescriptions = {
  dashboard: "Airworthiness gap analysis",
  chapters: "Systems by chapter",
  tasks: "Task intervals by model",
};

export const DEFAULT_MODE_SEGMENT = "dashboard";

/**
 * `<module>.viewMode` — matches the shape used across converted modules.
 */
export const MODE_STORAGE_KEY = "ata.viewMode";

/**
 * Is this a real mode segment?
 */
export const isModeSegment = (segment: string): boolean =>
  Object.prototype.hasOwnProperty.call(MODE_LABELS, segment);

/**
 * Redirect target for a bare visit to the module root.
 */
export function readRememberedModeSegment(): string {
  if (typeof window === "undefined") return DEFAULT_MODE_SEGMENT;
  let stored: string | null = null;
  try {
    stored = window.localStorage.getItem(MODE_STORAGE_KEY);
  } catch {
    return DEFAULT_MODE_SEGMENT;
  }
  stored = migrateModeSegment(stored); // "overview" -> "dashboard"
  return stored && isModeSegment(stored) ? stored : DEFAULT_MODE_SEGMENT;
}

/**
 * Split a live pathname into the module base and the mode segment.
 */
export function splitModePath(pathname: string): {
  basePath: string;
  currentMode: string;
  onMode: boolean;
} {
  const segments = pathname.split("/").filter(Boolean);
  const lastSegment = segments[segments.length - 1] ?? "";
  const onMode = isModeSegment(lastSegment);
  const baseSegments = onMode ? segments.slice(0, -1) : segments;
  return {
    basePath: baseSegments.length ? "/" + baseSegments.join("/") : "",
    currentMode: onMode ? lastSegment : DEFAULT_MODE_SEGMENT,
    onMode,
  };
}
